import * as React from 'react';
import { useLocation } from 'wouter';
import { CmdAlert, CmdLoader } from '@commander-services/gui-components';
import { useQuery } from '@tanstack/react-query';
import { useIntl } from 'react-intl';
import config from '../../../config';
import ResetPasswordService from './ResetPasswordService';
import Form from './Form';
import ForgotPasswordForm from '../ForgotPassword/ForgotPasswordLayout';
import { LanguageContextProps, defaultLanguage } from '../LanguagesContext';
import ForgotPasswordService from '../ForgotPassword/ForgotPasswordService';

interface IResetPasswordProps {
  context: LanguageContextProps;
}

const IS_STATUS_ERROR = 'error';
const IS_STATUS_OK = 'ok';
const IS_STATUS_EXPIRED = 'expired';

export default function ResetPassword(props: IResetPasswordProps) {
  const [_, navigate] = useLocation();
  const { formatMessage: f } = useIntl();
  const [isExpired, setIsExpired] = React.useState<boolean>(false);
  const [showExpirationToast, setShowExpirationToast] = React.useState<boolean>(false);

  const token = window.location.pathname.split('/').pop();

  const { isLoading, data } = useQuery({
    queryKey: ['reset-password'],
    queryFn: () => ResetPasswordService.getForm(token, defaultLanguage),
    staleTime: 9000,
    enabled: true,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });

  const { isLoading: isEmailFormLoading, data: emailFormData } = useQuery({
    queryKey: ['forgot-password'],
    queryFn: () => ForgotPasswordService.getForm(defaultLanguage),
    staleTime: 9000,
    enabled: isExpired,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });

  if (data && data.status === IS_STATUS_ERROR) {
    navigate(config.Login.forgotPasswordUrl);
  }

  React.useEffect(() => {
    if (token && data) {
      if (data.language && defaultLanguage !== data.language && props.context.updateValue) {
        props.context.updateValue('language', data.language);
      }
    }
    if (token && data && data.status === IS_STATUS_EXPIRED) {
      setIsExpired(true);
    }
    if (!isEmailFormLoading && isExpired && data) {
      setShowExpirationToast(true);
    }
  }, [data, isEmailFormLoading, isExpired, navigate, props.context, token]);

  const closeExpirationToast = () => {
    setShowExpirationToast(false);
  };

  return (
    <div className="w-login">
      {showExpirationToast && (
        <CmdAlert
          id="token-expired"
          data={{
            title: f({ id: 'toastr.warning' }),
            message: f({ id: 'toast.auth.resetPassword.expiredToken2' }),
            type: 'warning', // (data.toast.type as string).toLowerCase(),
          }}
          isAbsolute={true}
          // onCloseCallback={closeDeactivateWarning}
        />
      )}
      {(isLoading || isEmailFormLoading) && <CmdLoader inContent={true} />}
      {!isLoading &&
        data.status === IS_STATUS_OK &&
        data &&
        data.form &&
        token &&
        data.form.inputs &&
        data.form.inputs.length > 0 && <Form data={data.form.inputs} token={token} />}
      {!isEmailFormLoading &&
        emailFormData &&
        data &&
        isExpired &&
        emailFormData.status === IS_STATUS_OK && (
          <ForgotPasswordForm
            data={emailFormData.form.inputs}
            email={data.email || ''}
            resetPasswordToken={token}
            resetPasswordCallback={closeExpirationToast}
          />
        )}
    </div>
  );
}
